import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import './eLogbook.css'
import collegeConfig from '../../config/collegeConfig';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import {
  FaPlus,
  FaEye,
  FaBookMedical,
  FaClipboardList,
  FaSearch,
  FaSave,
  FaTrash,
  FaCheck,
  FaClock,
  FaExclamationTriangle,
  FaArrowLeft,
  FaEdit,
  FaFilter,
  FaTimes,
  FaSortAmountDown,
  FaSortAmountUp,
  FaDownload,
  FaChevronLeft,
  FaChevronRight,
  FaCalendarAlt,
  FaPaperclip,
  FaFilePdf,
  FaImage,
  FaSpinner
} from 'react-icons/fa';
import { generateLogbookPDF } from './pdfGenerator';

const ITEMS_PER_PAGE = 15;

const AttachmentItem = ({ attachment, token, baseURL }) => {
  const [loading, setLoading] = React.useState(false);
  const [viewerOpen, setViewerOpen] = React.useState(false);
  const [viewerUrl, setViewerUrl] = React.useState('');

  const handleOpen = async () => {
    setLoading(true);
    try {
      const t = token || localStorage.getItem('token');
      const url = baseURL || collegeConfig.api.baseURL;
      const res = await fetch(`${url}/upload/signed-url?key=${encodeURIComponent(attachment.key)}`, {
        headers: { Authorization: `Bearer ${t}` }
      });
      if (!res.ok) { setLoading(false); return; }
      const data = await res.json();

      if (Capacitor.isNativePlatform() && attachment.type !== 'image') {
        // PDF on native: download to cache and open with FileOpener
        const fileRes = await fetch(data.url);
        const blob = await fileRes.blob();
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64 = reader.result.split(',')[1];
          const fileName = attachment.name || 'attachment.pdf';
          const result = await Filesystem.writeFile({
            path: fileName,
            data: base64,
            directory: Directory.Cache,
            recursive: true
          });
          try {
            await FileOpener.open({ filePath: result.uri, contentType: 'application/pdf' });
          } catch {
            alert(`File saved. Open from Files app: ${fileName}`);
          }
          setLoading(false);
        };
        reader.readAsDataURL(blob);
        return;
      }

      setViewerUrl(data.url);
      setViewerOpen(true);
    } catch { /* ignore */ }
    setLoading(false);
  };

  return (
    <>
      <div className="attachment-item">
        <div className="attachment-icon">
          {attachment.type === 'image' ? <FaImage /> : <FaFilePdf />}
        </div>
        <span className="attachment-name">{attachment.name}</span>
        <button className="btn-view-attachment" onClick={handleOpen} disabled={loading}>
          {loading ? <FaSpinner className="spin" /> : 'View'}
        </button>
      </div>
      {viewerOpen && (
        <div className="attachment-viewer-overlay" onClick={() => setViewerOpen(false)}>
          <div className="attachment-viewer-content" onClick={e => e.stopPropagation()}>
            <div className="attachment-viewer-header">
              <span className="attachment-viewer-name">{attachment.name}</span>
              <button className="attachment-viewer-close" onClick={() => setViewerOpen(false)}>
                <FaTimes />
              </button>
            </div>
            {attachment.type === 'image'
              ? <img src={viewerUrl} alt={attachment.name} className="attachment-viewer-img" />
              : <iframe src={viewerUrl} title={attachment.name} className="attachment-viewer-iframe" />
            }
          </div>
        </div>
      )}
    </>
  );
};

const ELogbook = ({ onNavigate, parentUser, initialFilter }) => {
  const subscriptionExpired =
    parentUser?.subscription?.status !== 'not_required' &&
    (parentUser?.subscription?.status === 'expired' ||
      (parentUser?.subscription?.expiryDate && new Date(parentUser.subscription.expiryDate) < new Date()));
  const [view, setView] = useState('main');
  const [user, setUser] = useState(null);
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [moduleSearchTerm, setModuleSearchTerm] = useState('');
  const [formData, setFormData] = useState({});
  const [counters, setCounters] = useState({});
  const [submittedEntries, setSubmittedEntries] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [viewingEntry, setViewingEntry] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showDownloadOptions, setShowDownloadOptions] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]); // { file, preview, name, type, size }
  const [isUploading, setIsUploading] = useState(false);
  
  const [filters, setFilters] = useState({
    moduleId: '',
    sortOrder: 'desc',
    status: '',
    month: new Date().toISOString().slice(0, 7) // Current month in YYYY-MM format
  });
  const [showFilters, setShowFilters] = useState(true); // Changed to true - visible by default
  const [showAllTime, setShowAllTime] = useState(false); // New state for showing all entries
  const [toast, setToast] = useState({ show: false, message: '' });
  const monthInputRef = useRef(null); // Ref for month input
  const listContentRef = useRef(null); // Ref for scroll-to-top on page change
  const initialFilterAppliedRef = useRef(false); // Guards the flagged-entries deep-link mount effect

  // Attendance integration
  const [attendanceIntegrationEnabled, setAttendanceIntegrationEnabled] = useState(false);
  const [attendancePending, setAttendancePending] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [selectedAttendanceRecord, setSelectedAttendanceRecord] = useState(null);
  const [attendanceStatusFilter, setAttendanceStatusFilter] = useState('all');
  const [attendancePeriodFilter, setAttendancePeriodFilter] = useState('all');

  // Pagination state
  const [listPage, setListPage] = useState(1);
  const [monthListPages, setMonthListPages] = useState({});

  // Handler for PDF download
  const handleDownloadPDF = async (includeSubmissionDate) => {
    try {
      setShowDownloadOptions(false);
      setIsGeneratingPDF(true);
      setSuccess('Generating PDF...');

      const studentName = `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim() || 'Student';
      const courseName = course?.name || 'Course';

      // Filter only submitted/approved entries for PDF
      const pdfEntries = submittedEntries.filter(entry =>
        entry.status !== 'draft'
      );

      if (pdfEntries.length === 0) {
        setError('No submitted entries to generate PDF');
        setIsGeneratingPDF(false);
        return;
      }

      // Fetch achievements, dissertation and additional-info (address/mobile/blood
      // group/DOB/date of admission for the cover page) in parallel
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [achievementsRes, dissertationRes, additionalInfoRes] = await Promise.allSettled([
        fetch(`${collegeConfig.api.baseURL}/achievements`, { headers }).then(r => r.json()),
        fetch(`${collegeConfig.api.baseURL}/dissertation/my`, { headers }).then(r => r.json()),
        fetch(`${collegeConfig.api.baseURL}/additional-info/student`, { headers }).then(r => r.json()),
      ]);

      const achievements = achievementsRes.status === 'fulfilled' && achievementsRes.value.success
        ? achievementsRes.value.achievements || []
        : [];
      const dissertation = dissertationRes.status === 'fulfilled' && dissertationRes.value.success
        ? dissertationRes.value.dissertation
        : null;

      const additionalInfo = {};
      if (additionalInfoRes.status === 'fulfilled' && additionalInfoRes.value.success) {
        (additionalInfoRes.value.fields || []).forEach(f => {
          if (f.key && f.submission?.value) additionalInfo[f.key] = f.submission.value;
        });
      }

      const studentInfo = {
        email: user.email,
        profile: {
          registrationNumber: user.profile?.registrationNumber,
          batch: user.assignedCourses?.[0]?.batch || ''
        },
        additionalInfo
      };

      await generateLogbookPDF(studentName, courseName, modules, pdfEntries, {
        collegeName: collegeConfig.collegeName,
        shortName: collegeConfig.shortName,
        logo: collegeConfig.logo,
        theme: collegeConfig.theme,
        copyrightYear: collegeConfig.copyrightYear,
        appName: collegeConfig.appName
      }, studentInfo, { achievements, dissertation, includeSubmissionDate });

      setSuccess('PDF downloaded successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // ... rest of the component code remains exactly the same until the list view
  
  // Enhanced navigation function that updates browser history
  const navigateToView = (newView, state = {}) => {
    setView(newView);
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.querySelector('.elogbook-container')?.scrollTo({ top: 0, behavior: 'instant' });

    // Push state to browser history
    const historyState = {
      page: 'elogbook',
      view: newView,
      ...state
    };

    window.history.pushState(historyState, '', `#elogbook-${newView}`);
  };

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state) {
        // If state has elogbook page, handle internal navigation
        if (event.state.page === 'elogbook') {
          setView(event.state.view);
          
          // Restore state based on view
          if (event.state.selectedModule) {
            const module = modules.find(m => m.moduleId === event.state.selectedModule);
            setSelectedModule(module);
          } else {
            setSelectedModule(null);
          }
          
          if (event.state.viewingEntry) {
            const entry = submittedEntries.find(e => e._id === event.state.viewingEntry);
            setViewingEntry(entry);
          } else {
            setViewingEntry(null);
          }
          
          if (event.state.editingEntry) {
            const entry = submittedEntries.find(e => e._id === event.state.editingEntry);
            setEditingEntry(entry);
          } else {
            setEditingEntry(null);
          }
        } else {
          // If not elogbook state and we're on main view, go back to dashboard
          if (view === 'main') {
            if (onNavigate) {
              onNavigate('dashboard');
            }
          }
        }
      } else {
        // No state means we're going back to dashboard level
        if (view === 'main') {
          if (onNavigate) {
            onNavigate('dashboard');
          }
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    // Set initial state when component mounts
    if (!window.history.state || window.history.state.page !== 'elogbook') {
      window.history.pushState({ page: 'elogbook', view: 'main' }, '', '#elogbook-main');
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [view, modules, submittedEntries, onNavigate]);

  const groupFieldsByName = (fields) => {
    const grouped = {};
    const ungrouped = [];
    
    fields.forEach(field => {
      if (field.groupName) {
        if (!grouped[field.groupName]) {
          grouped[field.groupName] = [];
        }
        grouped[field.groupName].push(field);
      } else {
        ungrouped.push(field);
      }
    });
    
    return { grouped, ungrouped };
  };

  useEffect(() => {
    const userData = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (!token || !userData) {
      if (onNavigate) {
        onNavigate('login');
      }
      return;
    }
    
    try {
      const parsedUser = JSON.parse(userData);
      if (parsedUser.role !== 'student') {
        onNavigate('login');
        return;
      }
      setUser(parsedUser);
      fetchStudentCourse(parsedUser);
      // Check if attendance integration is enabled, then prefetch pending count for the dot
      const token = localStorage.getItem('token');
      fetch(`${collegeConfig.api.baseURL}/system-settings`)
        .then(r => r.json())
        .then(d => {
          if (d.success && d.attendanceIntegrationEnabled) {
            setAttendanceIntegrationEnabled(true);
            fetch(`${collegeConfig.api.baseURL}/attendance/pending`, {
              headers: { Authorization: `Bearer ${token}` }
            }).then(r => r.json()).then(p => { if (p.success) setAttendancePending(p.records || []); }).catch(() => {});
          }
        })
        .catch(() => {});
    } catch (error) {
      console.error('Error parsing user data:', error);
      if (onNavigate) {
        onNavigate('login');
      }
    }
  }, [onNavigate]);

  const fetchStudentCourse = async (userData) => {
    try {
      const token = localStorage.getItem('token');
      const userId = userData.id || userData._id;
      
      const response = await fetch(`${collegeConfig.api.baseURL}/students/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.student?.course) {
          setCourse(data.student.course);
          
          const courseResponse = await fetch(`${collegeConfig.api.baseURL}/courses/${data.student.course._id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (courseResponse.ok) {
            const courseData = await courseResponse.json();
            setModules(courseData.course.modules || []);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching course:', error);
      setError('Failed to load course information');
    } finally {
      setLoading(false);
    }
  };

  const fetchEntries = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const userId = user?.id || user?._id;
      
      if (!userId) return;
      
      const response = await fetch(`${collegeConfig.api.baseURL}/logbook/entries?studentId=${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubmittedEntries(data.entries || []);
      }
    } catch (error) {
      console.error('Error fetching entries:', error);
    }
  }, [user]);

  useEffect(() => {
    if (view === 'list' && user) {
      fetchEntries();
    }
  }, [view, user]);

  // Deep-link from the student dashboard's Repeat/Remedial alert banner
  useEffect(() => {
    if (initialFilter === 'flagged' && !initialFilterAppliedRef.current && user) {
      initialFilterAppliedRef.current = true;
      setFilters(prev => ({ ...prev, status: 'flagged' }));
      setShowAllTime(true);
      navigateToView('list', { fromFlaggedBanner: true });
    }
  }, [initialFilter, user]);

  const fetchAttendancePending = async () => {
    setAttendanceLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${collegeConfig.api.baseURL}/attendance/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      if (d.success) setAttendancePending(d.records || []);
    } catch {
      // silently fail — attendance is optional
    } finally {
      setAttendanceLoading(false);
    }
  };

  const getFilteredAndSortedEntries = () => {
    let filtered = [...submittedEntries];
    
    if (filters.moduleId) {
      filtered = filtered.filter(entry => entry.moduleId === filters.moduleId);
    }
    
    if (filters.status === 'flagged') {
      filtered = filtered.filter(entry =>
        entry.status === 'needs_revision' &&
        (['R', 'Re'].includes(entry.review?.facultyDecision) || !!entry.reattemptRequest)
      );
    } else if (filters.status) {
      filtered = filtered.filter(entry => entry.status === filters.status);
    }
    
    // Month filter logic - only apply if not showing all time
    if (filters.month && !showAllTime) {
      const currentMonth = new Date().toISOString().slice(0, 7);
      const selectedMonth = filters.month;
      
      filtered = filtered.filter(entry => {
        const entryMonth = new Date(entry.createdAt).toISOString().slice(0, 7);
        
        if (selectedMonth === currentMonth) {
          // Current month: show current month entries + previous months unverified entries
          if (entryMonth === currentMonth) {
            return true;
          } else if (entryMonth < currentMonth) {
            // Previous month entries - only show if not verified (approved)
            return entry.status !== 'approved';
          }
          return false;
        } else {
          // Previous month selected: show only that month's entries (all statuses)
          return entryMonth === selectedMonth;
        }
      });
    }
    
    filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return filters.sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });
    
    return filtered;
  };

  // Reset pagination when filters or mode change
  useEffect(() => {
    setListPage(1);
    setMonthListPages({});
  }, [filters.moduleId, filters.status, filters.month, filters.sortOrder, showAllTime]);

  // Group entries by month for "show all time" view
  const groupEntriesByMonth = (entries) => {
    const grouped = {};
    entries.forEach(entry => {
      const d = new Date(entry.createdAt);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const name = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      if (!grouped[key]) grouped[key] = { name, entries: [] };
      grouped[key].entries.push(entry);
    });
    return Object.entries(grouped).sort((a, b) => b[0].localeCompare(a[0]));
  };

  // Helper function to format month
  const formatMonthName = (monthString) => {
    if (!monthString) return '';
    const date = new Date(monthString + '-01');
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const filteredModules = modules.filter(module =>
    module.isActive !== false &&
    (module.name.toLowerCase().includes(moduleSearchTerm.toLowerCase()) ||
    (module.description && module.description.toLowerCase().includes(moduleSearchTerm.toLowerCase())))
  );

  const handleModuleCardClick = (module) => {
    setSelectedModule(module);
    setEditingEntry(null);
    navigateToView('fill-form', { selectedModule: module.moduleId });
    
    if (module.type === 'entries') {
      const initialData = {};
      module.fields.forEach(field => {
        initialData[field.key] = field.type === 'number' ? 0 : '';
      });
      setFormData(initialData);
    } else if (module.type === 'counters') {
      const initialCounters = {};
      module.fields.forEach(field => {
        if (field.type === 'counter') {
          initialCounters[field.key] = 0;
        }
      });
      setCounters(initialCounters);
    }
  };

  const handleFieldChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleCounterChange = (key, value) => {
    const numValue = parseInt(value) || 0;
    setCounters(prev => ({
      ...prev,
      [key]: numValue
    }));
  };

  const validateForm = () => {
    if (!selectedModule) {
      setError('Please select a module');
      return false;
    }

    if (selectedModule.type === 'entries') {
      // At least one field must have a value
      const hasAnyValue = selectedModule.fields.some(field => {
        const val = formData[field.key];
        return val !== undefined && val !== null && val !== '' && val !== 0;
      });
      if (!hasAnyValue) {
        setError('Please fill in at least one field before submitting.');
        return false;
      }
      // Required fields
      for (const field of selectedModule.fields) {
        if (field.required && !formData[field.key]) {
          setError(`"${field.label}" is required.`);
          return false;
        }
      }
    } else if (selectedModule.type === 'counters') {
      const hasAnyCounter = selectedModule.fields.some(field => (counters[field.key] || 0) > 0);
      if (!hasAnyCounter) {
        setError('Please enter a value greater than zero for at least one counter before submitting.');
        return false;
      }
    }

    return true;
  };

  // ── Attachment helpers ──────────────────────────────────────────────
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (!files.length) return;

    const MAX_BEFORE_COMPRESS = 8 * 1024 * 1024; // 8 MB
    const ALLOWED = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];

    for (const file of files) {
      if (!ALLOWED.includes(file.type)) {
        setError('Only JPG, PNG, GIF, WEBP images and PDF files are allowed.');
        return;
      }
      if (file.size > MAX_BEFORE_COMPRESS) {
        setError(`"${file.name}" is too large (max 8 MB).`);
        return;
      }
    }

    const processed = await Promise.all(files.map(async (file) => {
      const isImage = file.type.startsWith('image/');
      let finalFile = file;

      if (isImage) {
        try {
          const imageCompression = (await import('browser-image-compression')).default;
          finalFile = await imageCompression(file, {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
            onProgress: () => {}
          });
        } catch {
          finalFile = file; // fallback to original if compression fails
        }
      }

      return {
        file: finalFile,
        preview: isImage ? URL.createObjectURL(finalFile) : null,
        name: file.name,
        type: isImage ? 'image' : 'pdf',
        size: finalFile.size
      };
    }));

    setPendingFiles(prev => [...prev, ...processed]);
  };

  const removePendingFile = (index) => {
    setPendingFiles(prev => {
      const updated = [...prev];
      if (updated[index].preview) URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const uploadPendingFiles = async () => {
    if (!pendingFiles.length) return [];
    const token = localStorage.getItem('token');
    const uploaded = [];
    for (const pending of pendingFiles) {
      const fd = new FormData();
      fd.append('file', pending.file, pending.name);
      const res = await fetch(`${collegeConfig.api.baseURL}/upload/attachment`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      if (!res.ok) throw new Error(`Failed to upload ${pending.name}`);
      const data = await res.json();
      uploaded.push(data.attachment);
    }
    return uploaded;
  };

  const resetAttachments = () => {
    pendingFiles.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview); });
    setPendingFiles([]);
  };
  // ────────────────────────────────────────────────────────────────────

  // afterAction: 'list' | 'modules' | 'stay'
  const handleSubmit = async (isDraftSubmit = false, afterAction = 'list') => {
    if (!validateForm() && !isDraftSubmit) return;

    setError('');
    setIsUploading(true);
    try {
      // Upload any pending files first
      const uploadedAttachments = await uploadPendingFiles();

      const token = localStorage.getItem('token');
      const userId = user.id || user._id;

      const entryData = {
        studentId: userId,
        courseId: course._id,
        moduleId: selectedModule.moduleId,
        entryType: selectedModule.type === 'entries' ? 'single' : 'bulk',
        isDraft: isDraftSubmit,
        status: isDraftSubmit ? 'draft' : 'submitted',
        ...(selectedAttendanceRecord ? { attendanceRecordId: selectedAttendanceRecord._id } : {})
      };

      if (selectedModule.type === 'entries') {
        entryData.entries = [{
          data: formData,
          attachments: uploadedAttachments,
          submittedAt: new Date(),
          status: isDraftSubmit ? 'draft' : 'submitted'
        }];
      } else {
        entryData.counters = counters;
        if (uploadedAttachments.length > 0) {
          entryData.attachments = uploadedAttachments;
        }
      }

      const url = editingEntry
        ? `${collegeConfig.api.baseURL}/logbook/entries/${editingEntry._id}`
        : `${collegeConfig.api.baseURL}/logbook/entries`;

      const response = await fetch(url, {
        method: editingEntry ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(entryData)
      });

      if (response.ok) {
        fetchEntries();
        resetAttachments();
        if (selectedAttendanceRecord) setSelectedAttendanceRecord(null);

        if (editingEntry || afterAction === 'list') {
          const msg = editingEntry ? 'Entry updated successfully!' : 'Entry submitted successfully!';
          setSuccess(msg);
          navigateToView('list');
          resetForm();
        } else if (afterAction === 'modules') {
          showToast('Entry submitted! Choose your next module.');
          resetForm();
          navigateToView('select-module');
        } else if (afterAction === 'stay') {
          showToast('Entry submitted! Form is ready for your next entry.');
          resetFieldsOnly();
        }
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to submit entry');
      }
    } catch (error) {
      console.error('Error submitting entry:', error);
      setError('Error connecting to server');
    } finally {
      setIsUploading(false);
    }
  };

  const handleEdit = (entry) => {
    const module = modules.find(m => m.moduleId === entry.moduleId);
    if (!module) {
      setError('Module not found');
      return;
    }
    
    setSelectedModule(module);
    setEditingEntry(entry);
    
    if (entry.entryType === 'single' && entry.entries && entry.entries[0]) {
      const entryData = entry.entries[0].data;
      const formDataObj = {};
      if (entryData instanceof Map) {
        entryData.forEach((value, key) => {
          formDataObj[key] = value;
        });
      } else {
        Object.assign(formDataObj, entryData);
      }
      setFormData(formDataObj);
    } else if (entry.entryType === 'bulk' && entry.counters) {
      const countersObj = {};
      if (entry.counters instanceof Map) {
        entry.counters.forEach((value, key) => {
          countersObj[key] = value;
        });
      } else {
        Object.assign(countersObj, entry.counters);
      }
      setCounters(countersObj);
    }
    
    navigateToView('fill-form', { 
      selectedModule: module.moduleId, 
      editingEntry: entry._id 
    });
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/logbook/entries/${entryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setSuccess('Entry deleted successfully');
        fetchEntries();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to delete entry');
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
      setError('Error connecting to server');
    }
  };

  const handleViewDetails = (entry) => {
    setViewingEntry(entry);
    navigateToView('view-details', { viewingEntry: entry._id });
  };

  const resetForm = () => {
    setFormData({});
    setCounters({});
    setSelectedModule(null);
    setModuleSearchTerm('');
    setEditingEntry(null);
  };

  const showToast = (message) => {
    setToast({ show: true, message });
    setTimeout(() => setToast({ show: false, message: '' }), 3500);
  };

  // Resets only the field values, keeping the same module open
  const resetFieldsOnly = () => {
    if (selectedModule?.type === 'entries') {
      const initial = {};
      selectedModule.fields.forEach(f => { initial[f.key] = f.type === 'number' ? 0 : ''; });
      setFormData(initial);
    } else if (selectedModule?.type === 'counters') {
      const initial = {};
      selectedModule.fields.forEach(f => { if (f.type === 'counter') initial[f.key] = 0; });
      setCounters(initial);
    }
    setEditingEntry(null);
  };

  const renderField = (field) => {
    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={formData[field.key] || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={`Enter ${field.label}`}
            required={field.required}
          />
        );
      
      case 'number':
        return (
          <input
            type="number"
            value={formData[field.key] || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={`Enter ${field.label}`}
            min={field.validation?.min}
            max={field.validation?.max}
            required={field.required}
          />
        );
      
      case 'date':
        return (
          <input
            type="date"
            value={formData[field.key] || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            required={field.required}
          />
        );
      
      case 'select':
        return (
          <select
            value={formData[field.key] || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            required={field.required}
          >
            <option value="">Select {field.label}</option>
            {field.options?.map((option, idx) => (
              <option key={idx} value={option}>{option}</option>
            ))}
          </select>
        );
      
      case 'textarea':
        return (
          <textarea
            value={formData[field.key] || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={`Enter ${field.label}`}
            rows="4"
            required={field.required}
          />
        );
      
      case 'counter':
        return (
          <div className="counter-field">
            <input
              type="number"
              value={counters[field.key] || 0}
              onChange={(e) => handleCounterChange(field.key, e.target.value)}
              min="0"
            />
            {field.target && (
              <span className="target-info">Target: {field.target}</span>
            )}
          </div>
        );
      
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { icon: <FaClock />, class: 'status-draft', text: 'Draft' },
      submitted: { icon: <FaClock />, class: 'status-submitted', text: 'Submitted' },
      approved: { icon: <FaCheck />, class: 'status-approved', text: 'Approved' },
      rejected: { icon: <FaExclamationTriangle />, class: 'status-rejected', text: 'Rejected' },
      needs_revision: { icon: <FaExclamationTriangle />, class: 'status-rejected', text: 'Needs Revision' },
      under_review: { icon: <FaClock />, class: 'status-pending', text: 'Under Review' }
    };
    
    return badges[status] || badges.submitted;
  };

  const canEdit = (entry) => {
    return entry.status === 'draft' || entry.status === 'needs_revision' || entry.status === 'rejected';
  };

  const canDelete = (entry) => {
    return entry.status === 'draft' || entry.status === 'rejected';
  };

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (loading) {
    return (
      <div className="elogbook-loading">
        <div className="spinner"></div>
        <p>Loading eLogbook...</p>
      </div>
    );
  }

  if (view === 'view-details' && viewingEntry) {
    const module = modules.find(m => m.moduleId === viewingEntry.moduleId);
    const status = getStatusBadge(viewingEntry.status);
    const { grouped, ungrouped } = module ? groupFieldsByName(module.fields) : { grouped: {}, ungrouped: [] };
    
    return (
      <div className="elogbook-container">
        <div className="elogbook-header">
          <button className="back-button" onClick={() => {
            setViewingEntry(null);
            navigateToView('list');
          }}>
            <FaArrowLeft /> Back to Submissions
          </button>
          <h1>Entry Details</h1>
          <p>{module?.name || 'Unknown Module'}</p>
        </div>

        <div className="entry-details-card">
          <div className="details-header">
            <h2>{module?.name}</h2>
            <div className={`status-badge ${status.class}`}>
              {status.icon} {status.text}
            </div>
          </div>

          <div className="details-meta">
            <div className="meta-item">
              <strong>Submitted:</strong>
              <span>{new Date(viewingEntry.createdAt).toLocaleString()}</span>
            </div>
            {viewingEntry.review && viewingEntry.status === 'approved' && (
              <>
                <div className="meta-item">
                  <strong>Reviewed By:</strong>
                  <span>{viewingEntry.review.reviewerName || 'Unknown'}</span>
                </div>
                <div className="meta-item">
                  <strong>Reviewed On:</strong>
                  <span>{new Date(viewingEntry.review.reviewedAt).toLocaleString()}</span>
                </div>
              </>
            )}
          </div>

          <div className="details-content">
            <h3>Submitted Data</h3>
            {viewingEntry.entryType === 'single' && viewingEntry.entries && viewingEntry.entries[0] && (
              <>
                {ungrouped.length > 0 && (
                  <div className="data-grid">
                    {ungrouped.map(field => {
                      const entryData = viewingEntry.entries[0].data;
                      let value;
                      if (entryData instanceof Map) {
                        value = entryData.get(field.key);
                      } else {
                        value = entryData[field.key];
                      }
                      
                      return (
                        <div key={field.key} className="data-item">
                          <strong>{field.label}:</strong>
                          <span>{value || 'N/A'}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {Object.entries(grouped).map(([groupName, groupFields]) => (
                  <div key={groupName} className="field-group-container" style={{ marginTop: '20px' }}>
                    <div className="field-group-header">
                      <h4>{groupName}</h4>
                    </div>
                    <div className="data-grid" style={{ padding: '15px', background: 'white' }}>
                      {groupFields.map(field => {
                        const entryData = viewingEntry.entries[0].data;
                        let value;
                        if (entryData instanceof Map) {
                          value = entryData.get(field.key);
                        } else {
                          value = entryData[field.key];
                        }
                        
                        return (
                          <div key={field.key} className="data-item">
                            <strong>{field.label}:</strong>
                            <span>{value || 'N/A'}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </>
            )}
            
            {viewingEntry.entryType === 'bulk' && viewingEntry.counters && (
              <div className="data-grid">
                {module?.fields.map(field => {
                  let value;
                  if (viewingEntry.counters instanceof Map) {
                    value = viewingEntry.counters.get(field.key);
                  } else {
                    value = viewingEntry.counters[field.key];
                  }
                  
                  return (
                    <div key={field.key} className="data-item">
                      <strong>{field.label}:</strong>
                      <span>{value || 0}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {(() => {
            const atts = viewingEntry.entryType === 'single'
              ? viewingEntry.entries?.[0]?.attachments
              : viewingEntry.attachments;
            const token = localStorage.getItem('token');
            if (!atts || atts.length === 0) return null;
            return (
              <div className="details-attachments">
                <h3><FaPaperclip /> Attachments</h3>
                <div className="attachments-list">
                  {atts.map((att, i) => (
                    <AttachmentItem
                      key={i}
                      attachment={att}
                      token={token}
                      baseURL={collegeConfig.api.baseURL}
                    />
                  ))}
                </div>
              </div>
            );
          })()}

          {viewingEntry.review && viewingEntry.review.reviewerId && viewingEntry.status === 'approved' && (
            <div className="review-section">
              <h3>Verification Details</h3>
              <div className="review-grid">
                {viewingEntry.review.attemptActivity && (
                  <div className="review-item">
                    <strong>Attempt Activity:</strong>
                    <span>{viewingEntry.review.attemptActivity === 'F' ? 'First/Only' :
                           viewingEntry.review.attemptActivity === 'R' ? 'Repeat' : 'Remedial'}</span>
                  </div>
                )}
                {viewingEntry.review.rating && (
                  <div className="review-item">
                    <strong>Rating:</strong>
                    <span>{viewingEntry.review.rating === 'E' ? 'Exceeds Expectations' :
                           viewingEntry.review.rating === 'M' ? 'Meets Expectations' : 'Below Expectations'}</span>
                  </div>
                )}
                {viewingEntry.review.facultyDecision && (
                  <div className="review-item">
                    <strong>Faculty Decision:</strong>
                    <span>{viewingEntry.review.facultyDecision === 'C' ? 'Completed' :
                           viewingEntry.review.facultyDecision === 'R' ? 'Repeat' : 'Remedial'}</span>
                  </div>
                )}
                {viewingEntry.review.feedback && (
                  <div className="review-item full-width">
                    <strong>Feedback:</strong>
                    <p>{viewingEntry.review.feedback}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {viewingEntry.reattemptRequest && viewingEntry.status === 'needs_revision' && (
            <div className="review-section">
              <h3>Reattempt Requested</h3>
              <div className="review-grid">
                <div className="review-item">
                  <strong>Requested By:</strong>
                  <span>{viewingEntry.reattemptRequest.requestedByName}</span>
                </div>
                <div className="review-item">
                  <strong>Requested On:</strong>
                  <span>{new Date(viewingEntry.reattemptRequest.requestedAt).toLocaleString()}</span>
                </div>
                {viewingEntry.reattemptRequest.note && (
                  <div className="review-item full-width">
                    <strong>Note:</strong>
                    <p>{viewingEntry.reattemptRequest.note}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {viewingEntry.reviewHistory?.length > 0 && (
            <div className="review-section">
              <h3>Previous Attempt{viewingEntry.reviewHistory.length > 1 ? 's' : ''}</h3>
              {viewingEntry.reviewHistory.map((h, i) => (
                <div key={i} className="review-grid" style={{ marginBottom: 12 }}>
                  <div className="review-item">
                    <strong>Attempt:</strong>
                    <span>
                      #{i + 1}{h.reason === 'reverified' ? ' — Re-verified by different faculty' : h.reason === 'hod_reattempt' ? ' — HOD requested reattempt' : ''}
                    </span>
                  </div>
                  {h.attemptActivity && (
                    <div className="review-item">
                      <strong>Attempt Activity:</strong>
                      <span>{h.attemptActivity === 'F' ? 'First/Only' : h.attemptActivity === 'R' ? 'Repeat' : 'Remedial'}</span>
                    </div>
                  )}
                  {h.rating && (
                    <div className="review-item">
                      <strong>Rating:</strong>
                      <span>{h.rating === 'E' ? 'Exceeds Expectations' : h.rating === 'M' ? 'Meets Expectations' : 'Below Expectations'}</span>
                    </div>
                  )}
                  {h.facultyDecision && (
                    <div className="review-item">
                      <strong>Faculty Decision:</strong>
                      <span>{h.facultyDecision === 'C' ? 'Completed' : h.facultyDecision === 'R' ? 'Repeat' : 'Remedial'}</span>
                    </div>
                  )}
                  {h.feedback && (
                    <div className="review-item full-width">
                      <strong>Feedback:</strong>
                      <p>{h.feedback}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="details-actions">
            {canEdit(viewingEntry) && (
              <button className="btn-edit11" onClick={() => handleEdit(viewingEntry)}>
                <FaEdit /> Edit Entry
              </button>
            )}
            {canDelete(viewingEntry) && (
              <button className="btn-delete11" onClick={() => {
                handleDelete(viewingEntry._id);
                setViewingEntry(null);
                navigateToView('list');
              }}>
                <FaTrash /> Delete Entry
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (view === 'main') {
    return (
      <div className="elogbook-container">
        <div className="elogbook-header">
          <button className="back-button" onClick={() => onNavigate && onNavigate('dashboard')}>
            ← Back to Dashboard
          </button>
          <h1>eLogbook</h1>
          <p>Manage your clinical competencies and submissions</p>
        </div>

        {success && <div className="alert alert-success">{success}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        <div className="elogbook-options">
          {subscriptionExpired ? (
            <div className="option-card" style={{ opacity: 0.6, cursor: 'not-allowed', background: '#fafafa' }}>
              <div className="option-icon" style={{ background: '#bbb' }}>
                🔒
              </div>
              <h3>Add Competency</h3>
              <p>Renew your subscription to log new entries</p>
            </div>
          ) : (
            <div className="option-card" onClick={() => navigateToView('select-module')}>
              <div className="option-icon" style={{ background: '#48C9B0' }}>
                <FaPlus />
              </div>
              <h3>Add Competency</h3>
              <p>Log new clinical competency entry</p>
            </div>
          )}

          {attendanceIntegrationEnabled && !subscriptionExpired && (
            <div className="option-card" onClick={() => { fetchAttendancePending(); navigateToView('attendance-list'); }} style={{ position: 'relative' }}>
              {attendancePending.some(r => r.attendance_status !== 'Absent') && (
                <span style={{
                  position: 'absolute', top: 10, right: 10,
                  width: 11, height: 11, borderRadius: '50%',
                  background: '#e53e3e',
                  boxShadow: '0 0 0 0 rgba(229,62,62,0.7)',
                  animation: 'attendance-pulse 1.4s infinite'
                }} />
              )}
              <style>{`
                @keyframes attendance-pulse {
                  0%   { box-shadow: 0 0 0 0 rgba(229,62,62,0.7); }
                  70%  { box-shadow: 0 0 0 8px rgba(229,62,62,0); }
                  100% { box-shadow: 0 0 0 0 rgba(229,62,62,0); }
                }
              `}</style>
              <div className="option-icon" style={{ background: '#6C3483' }}>
                <FaCalendarAlt />
              </div>
              <h3>Fill from Attendance</h3>
              <p>Create logbook entries from your attended lectures</p>
            </div>
          )}

          <div className="option-card" onClick={() => navigateToView('list')}>
            <div className="option-icon" style={{ background: '#3498DB' }}>
              <FaClipboardList />
            </div>
            <h3>View Submissions</h3>
            <p>Review your submitted entries</p>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'attendance-list') {
    const formatDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    const isAbsent = (r) => r.attendance_status === 'Absent';

    const cutoffDate = (() => {
      if (attendancePeriodFilter === '7') { const d = new Date(); d.setDate(d.getDate() - 7); return d; }
      if (attendancePeriodFilter === '30') { const d = new Date(); d.setDate(d.getDate() - 30); return d; }
      return null;
    })();

    const filteredAttendance = attendancePending.filter(r => {
      if (attendanceStatusFilter === 'fillable' && isAbsent(r)) return false;
      if (attendanceStatusFilter === 'absent' && !isAbsent(r)) return false;
      if (cutoffDate && new Date(r.date_of_attendance) < cutoffDate) return false;
      return true;
    });

    const fillableCount = attendancePending.filter(r => !isAbsent(r)).length;

    return (
      <div className="elogbook-container">
        <div className="elogbook-header">
          <button className="back-button" onClick={() => { setSelectedAttendanceRecord(null); navigateToView('main'); }}>
            <FaArrowLeft /> Back
          </button>
          <h1>Fill from Attendance</h1>
          <p>{fillableCount} lecture{fillableCount !== 1 ? 's' : ''} pending · tap to fill logbook</p>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 6, background: '#f3f4f6', borderRadius: 10, padding: 4 }}>
            {[['all', 'All'], ['fillable', 'Fillable'], ['absent', 'Absent']].map(([val, label]) => (
              <button key={val} onClick={() => setAttendanceStatusFilter(val)} style={{
                padding: '5px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                background: attendanceStatusFilter === val ? '#6C3483' : 'transparent',
                color: attendanceStatusFilter === val ? '#fff' : '#555',
                transition: 'all 0.15s'
              }}>{label}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6, background: '#f3f4f6', borderRadius: 10, padding: 4 }}>
            {[['all', 'All Time'], ['7', 'Last 7 Days'], ['30', 'Last 30 Days']].map(([val, label]) => (
              <button key={val} onClick={() => setAttendancePeriodFilter(val)} style={{
                padding: '5px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                background: attendancePeriodFilter === val ? '#6C3483' : 'transparent',
                color: attendancePeriodFilter === val ? '#fff' : '#555',
                transition: 'all 0.15s'
              }}>{label}</button>
            ))}
          </div>
        </div>

        {attendanceLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <FaSpinner style={{ fontSize: 28, color: '#6C3483', animation: 'spin 1s linear infinite' }} />
            <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
          </div>
        ) : filteredAttendance.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 24px', color: '#888' }}>
            <FaCalendarAlt style={{ fontSize: 40, marginBottom: 16, color: '#ccc' }} />
            <h3 style={{ margin: '0 0 8px', color: '#555' }}>No records found</h3>
            <p style={{ margin: 0, fontSize: 14 }}>
              {attendancePending.length === 0
                ? 'All attended lectures already have logbook entries, or no attendance data has been pushed yet.'
                : 'No records match the selected filters.'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '0 0 24px' }}>
            {filteredAttendance.map(record => {
              const absent = isAbsent(record);
              return (
                <div
                  key={record._id}
                  onClick={() => {
                    if (absent) return;
                    setSelectedAttendanceRecord(record);
                    navigateToView('select-module');
                  }}
                  style={{
                    background: absent ? '#fafafa' : '#fff',
                    border: `1px solid ${absent ? '#fecaca' : '#e5e7eb'}`,
                    borderLeft: `4px solid ${absent ? '#ef4444' : '#6C3483'}`,
                    borderRadius: 12,
                    padding: '16px 18px',
                    cursor: absent ? 'default' : 'pointer',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                    opacity: absent ? 0.75 : 1
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 15, color: absent ? '#9ca3af' : '#1a1a2e', marginBottom: 6 }}>
                    {record.lecture_name}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', fontSize: 13, color: absent ? '#9ca3af' : '#555' }}>
                    <span><FaCalendarAlt style={{ marginRight: 5, color: absent ? '#d1d5db' : '#6C3483' }} />{formatDate(record.date_of_attendance)}</span>
                    {record.faculty_name && <span>Faculty: {record.faculty_name}</span>}
                    <span style={{
                      background: absent ? '#fee2e2' : record.attendance_status === 'Present' ? '#d1fae5' : '#fef3c7',
                      color: absent ? '#991b1b' : record.attendance_status === 'Present' ? '#065f46' : '#92400e',
                      padding: '1px 10px', borderRadius: 20, fontWeight: 600, fontSize: 12
                    }}>
                      {record.attendance_status}
                    </span>
                  </div>
                  {absent && (
                    <div style={{ marginTop: 10, fontSize: 12, color: '#ef4444', fontWeight: 500 }}>
                      You were marked absent — logbook entry cannot be created for this lecture.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (view === 'select-module') {
    return (
      <div className="elogbook-container">
        <div className="elogbook-header">
          <button className="back-button" onClick={() => {
            if (selectedAttendanceRecord) {
              setSelectedAttendanceRecord(null);
              navigateToView('attendance-list');
            } else {
              navigateToView('main');
            }
          }}>
            <FaArrowLeft /> Back
          </button>
          <h1>Select Module</h1>
          <p>Choose a module to add your competency entry</p>
        </div>

        {selectedAttendanceRecord && (
          <div style={{ background: '#f3e8ff', border: '1px solid #c4b5fd', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#5b21b6', lineHeight: 1.6 }}>
            <strong>Attendance context:</strong> {selectedAttendanceRecord.lecture_name} &nbsp;·&nbsp;
            {new Date(selectedAttendanceRecord.date_of_attendance).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            {selectedAttendanceRecord.faculty_name && ` · Faculty: ${selectedAttendanceRecord.faculty_name}`}
            &nbsp;·&nbsp; <strong>{selectedAttendanceRecord.attendance_status}</strong>
          </div>
        )}

        {success && <div className="alert alert-success">{success}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        <div className="module-selection-container">
          <div className="module-search-bar">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search modules by name or description..."
              value={moduleSearchTerm}
              onChange={(e) => setModuleSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="modules-grid">
            {filteredModules.length === 0 ? (
              <div className="no-modules-found">
                <FaBookMedical className="no-modules-icon" />
                <h3>No modules found</h3>
                <p>Try adjusting your search terms</p>
              </div>
            ) : (
              filteredModules.map((module) => (
                <div
                  key={module.moduleId}
                  className="module-card"
                  onClick={() => handleModuleCardClick(module)}
                >
                  <div className="module-card-header">
                    <div className="module-icon">
                      <FaBookMedical />
                    </div>
                  </div>
                  
                  <h3 className="module-card-title">{module.name}</h3>
                  
                  {module.description && (
                    <p className="module-card-description">{module.description}</p>
                  )}
                  
                  {module.requirements && (
                    <div className="module-requirements">
                      <div className="requirement-item">
                        <span className="requirement-label">Min Entries:</span>
                        <span className="requirement-value">{module.requirements.minEntries}</span>
                      </div>
                      <div className="requirement-item">
                        <span className="requirement-label">Total Marks:</span>
                        <span className="requirement-value">{module.requirements.totalMarks}</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="module-card-footer">
                    <span className="fields-count">{module.fields.length} fields</span>
                    <FaArrowLeft className="arrow-icon" style={{ transform: 'rotate(180deg)' }} />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {toast.show && (
          <div className="submission-toast">
            <FaCheck className="toast-icon" />
            {toast.message}
          </div>
        )}
      </div>
    );
  }

  if (view === 'fill-form') {
    const { grouped, ungrouped } = groupFieldsByName(selectedModule.fields);
    
    return (
      <div className="elogbook-container">
        <div className="elogbook-header">
          <button className="back-button" onClick={() => {
            resetForm();
            navigateToView('select-module');
          }}>
            <FaArrowLeft /> Back to Modules
          </button>
          <h1>{editingEntry ? 'Edit Entry' : selectedModule.name}</h1>
          <p>{editingEntry ? 'Update your entry' : 'Fill in the required information'}</p>
        </div>

        {success && <div className="alert alert-success">{success}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        {selectedAttendanceRecord && (
          <div style={{ background: '#f3e8ff', border: '1px solid #c4b5fd', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#5b21b6', lineHeight: 1.7 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Attendance Record</div>
            <div><strong>Lecture:</strong> {selectedAttendanceRecord.lecture_name}</div>
            <div><strong>Date:</strong> {new Date(selectedAttendanceRecord.date_of_attendance).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
            {selectedAttendanceRecord.faculty_name && <div><strong>Faculty:</strong> {selectedAttendanceRecord.faculty_name}</div>}
            <div><strong>Status:</strong> {selectedAttendanceRecord.attendance_status}</div>
          </div>
        )}

        <div className="competency-form">
          <div className="form-section">
            <h3>{selectedModule.name}</h3>
            <div className="form-fields">
              {ungrouped.map((field) => (
                <div key={field.key} className="form-group">
                  <label>
                    {field.label}
                    {field.required && <span className="required">*</span>}
                  </label>
                  {renderField(field)}
                </div>
              ))}
              
              {Object.entries(grouped).map(([groupName, groupFields]) => (
                <div key={groupName} className="field-group-container">
                  <div className="field-group-header">
                    <h4>{groupName}</h4>
                  </div>
                  <div className="field-group-content">
                    {groupFields.map((field) => (
                      <div key={field.key} className="form-group grouped-field">
                        <label>
                          {field.label}
                          {field.required && <span className="required">*</span>}
                        </label>
                        {renderField(field)}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {selectedModule.allowAttachments && (
              <div className="attachment-upload-section">
                <div className="attachment-upload-header">
                  <FaPaperclip /> Attachments
                  {pendingFiles.length > 0 && (
                    <span className="attachment-count">{pendingFiles.length} file{pendingFiles.length > 1 ? 's' : ''} selected</span>
                  )}
                </div>

                {pendingFiles.length > 0 && (
                  <div className="pending-files-list">
                    {pendingFiles.map((pf, idx) => (
                      <div key={idx} className="pending-file-item">
                        {pf.type === 'image' && pf.preview
                          ? <img src={pf.preview} alt={pf.name} className="pending-file-thumb" />
                          : <div className="pending-file-icon"><FaFilePdf /></div>
                        }
                        <span className="pending-file-name">{pf.name}</span>
                        <span className="pending-file-size">{(pf.size / 1024).toFixed(0)} KB</span>
                        <button className="pending-file-remove" onClick={() => removePendingFile(idx)} title="Remove">
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <label className="attachment-upload-btn">
                  <FaPaperclip /> Add Files
                  <input
                    type="file"
                    multiple
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,application/pdf"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                    disabled={isUploading}
                  />
                </label>
                <small className="attachment-hint">Images are compressed automatically. Max 8 MB per file.</small>
              </div>
            )}

            {editingEntry ? (
              <div className="form-actions">
                <button className="btn-submit" onClick={() => handleSubmit(false, 'list')} disabled={isUploading}>
                  {isUploading ? <><FaSpinner className="spin" /> Uploading...</> : <><FaSave /> Update Entry</>}
                </button>
                <button className="btn-cancel" onClick={() => { resetForm(); navigateToView('list'); }}>
                  Cancel
                </button>
              </div>
            ) : (
              <div className="form-actions-new">
                {isUploading && (
                  <div className="upload-progress-bar">
                    <FaSpinner className="spin" /> Uploading files, please wait…
                  </div>
                )}
                <div className="form-actions-triple">
                  <button className="btn-submit-action btn-action-list" onClick={() => handleSubmit(false, 'list')} disabled={isUploading}>
                    <FaClipboardList className="btn-action-icon" />
                    <div className="btn-action-text">
                      <span>Submit &amp; View Submissions</span>
                      <small>Go to your submissions list</small>
                    </div>
                  </button>
                  <button className="btn-submit-action btn-action-modules" onClick={() => handleSubmit(false, 'modules')} disabled={isUploading}>
                    <FaBookMedical className="btn-action-icon" />
                    <div className="btn-action-text">
                      <span>Submit &amp; Choose Another Module</span>
                      <small>Submit and pick a different module</small>
                    </div>
                  </button>
                  <button className="btn-submit-action btn-action-stay" onClick={() => handleSubmit(false, 'stay')} disabled={isUploading}>
                    <FaPlus className="btn-action-icon" />
                    <div className="btn-action-text">
                      <span>Submit &amp; Add Another Entry</span>
                      <small>Stay here and add one more entry</small>
                    </div>
                  </button>
                </div>
                <button className="btn-cancel" onClick={() => { resetForm(); navigateToView('select-module'); }}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {toast.show && (
          <div className="submission-toast">
            <FaCheck className="toast-icon" />
            {toast.message}
          </div>
        )}
      </div>
    );
  }

  if (view === 'list') {
    const filteredEntries = getFilteredAndSortedEntries();

    const renderCard = (entry) => {
      const module = modules.find(m => m.moduleId === entry.moduleId);
      const status = getStatusBadge(entry.status || 'submitted');
      const isFlagged = entry.status === 'needs_revision' &&
        (['R', 'Re'].includes(entry.review?.facultyDecision) || !!entry.reattemptRequest);
      return (
        <div key={entry._id} className={`submission-card ${isFlagged ? 'submission-card-flagged' : ''}`}>
          <div className="submission-header">
            <h4>{module?.name || 'Unknown Module'}</h4>
            <div className={`status-badge ${status.class}`}>
              {status.icon} {status.text}
            </div>
          </div>
          <div className="submission-body">
            <div className="submission-meta">
              <span>Submitted: {new Date(entry.createdAt).toLocaleDateString()}</span>
            </div>
            {entry.attendanceRevoked && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '8px 12px', marginBottom: 8, fontSize: 13 }}>
                <FaExclamationTriangle style={{ color: '#ea580c', flexShrink: 0, marginTop: 2 }} />
                <div>
                  <strong style={{ color: '#9a3412' }}>Attendance Corrected</strong>
                  <div style={{ color: '#c2410c', marginTop: 2 }}>{entry.attendanceRevokedNote || 'Your attendance for the linked lecture was revised by faculty.'} Please review and update this entry if needed.</div>
                </div>
              </div>
            )}
            {!entry.attendanceRevoked && entry.attendanceSource?.lecture_name && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f3e8ff', border: '1px solid #d8b4fe', borderRadius: 8, padding: '6px 12px', marginBottom: 8, fontSize: 13, color: '#6b21a8' }}>
                <FaCalendarAlt style={{ flexShrink: 0 }} />
                <span><strong>Via Attendance:</strong> {entry.attendanceSource.lecture_name}
                  {entry.attendanceSource.date_of_attendance && ` · ${new Date(entry.attendanceSource.date_of_attendance).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                </span>
              </div>
            )}
            {(() => {
              const attCount = entry.entryType === 'single'
                ? (entry.entries?.[0]?.attachments?.length || 0)
                : (entry.attachments?.length || 0);
              if (attCount === 0) return null;
              return (
                <div className="attachment-indicator">
                  <FaPaperclip /> {attCount} item{attCount > 1 ? 's' : ''} attached &middot; click <strong>View Details</strong> to see
                </div>
              );
            })()}
            {entry.review && entry.review.reviewerId && entry.status === 'approved' && (
              <div className="review-info">
                <div className="review-details">
                  <p><strong>Reviewed by:</strong> {entry.review.reviewerName}</p>
                  {entry.review.feedback && (
                    <p className="review-feedback"><strong>Feedback:</strong> {entry.review.feedback}</p>
                  )}
                </div>
              </div>
            )}
            <div className="submission-actions">
              <button className="btn-view-details" onClick={() => handleViewDetails(entry)}>
                <FaEye /> View Details
              </button>
              {(entry.status === 'draft' || entry.status === 'needs_revision' || entry.status === 'rejected' || entry.status === 'submitted') && (
                <button className={isFlagged ? 'btn-reattempt' : 'btn-edit-entry'} onClick={() => handleEdit(entry)}>
                  <FaEdit /> {isFlagged ? 'Reattempt' : 'Edit'}
                </button>
              )}
              {(entry.status === 'draft' || entry.status === 'rejected' || entry.status === 'submitted') && (
                <button className="btn-delete-entry" onClick={() => handleDelete(entry._id)}>
                  <FaTrash /> Delete
                </button>
              )}
            </div>
          </div>
        </div>
      );
    };

    const renderPaginationBar = (currentPage, totalPages, onPrev, onNext) => (
      <div className="el-pagination-controls">
        <button className="el-page-btn" disabled={currentPage === 1} onClick={onPrev}>
          <FaChevronLeft /> Prev
        </button>
        <span className="el-page-info">Page {currentPage} of {totalPages}</span>
        <button className="el-page-btn" disabled={currentPage === totalPages} onClick={onNext}>
          Next <FaChevronRight />
        </button>
      </div>
    );

    const scrollToContent = () => {
      setTimeout(() => listContentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
    };

    const renderFlatPaginated = () => {
      const totalPages = Math.ceil(filteredEntries.length / ITEMS_PER_PAGE);
      const start = (listPage - 1) * ITEMS_PER_PAGE;
      const pageEntries = filteredEntries.slice(start, start + ITEMS_PER_PAGE);
      return (
        <>
          <div className="submissions-grid">{pageEntries.map(renderCard)}</div>
          {totalPages > 1 && renderPaginationBar(
            listPage, totalPages,
            () => { setListPage(p => p - 1); scrollToContent(); },
            () => { setListPage(p => p + 1); scrollToContent(); }
          )}
        </>
      );
    };

    const renderGroupedByMonth = () => {
      const groups = groupEntriesByMonth(filteredEntries);
      return groups.map(([monthKey, monthData]) => {
        const currentPage = monthListPages[monthKey] || 1;
        const totalPages = Math.ceil(monthData.entries.length / ITEMS_PER_PAGE);
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        const pageEntries = monthData.entries.slice(start, start + ITEMS_PER_PAGE);
        return (
          <div key={monthKey} className="el-month-group">
            <div className="el-month-header">
              <FaCalendarAlt />
              <h3>{monthData.name}</h3>
              <span className="el-month-count">{monthData.entries.length} entries</span>
            </div>
            <div className="submissions-grid">{pageEntries.map(renderCard)}</div>
            {totalPages > 1 && renderPaginationBar(
              currentPage, totalPages,
              () => { setMonthListPages(p => ({ ...p, [monthKey]: currentPage - 1 })); scrollToContent(); },
              () => { setMonthListPages(p => ({ ...p, [monthKey]: currentPage + 1 })); scrollToContent(); }
            )}
          </div>
        );
      });
    };

    return (
      <div className="elogbook-container">
        <div className="elogbook-header">
          <button className="back-button" onClick={() => navigateToView('main')}>
            <FaArrowLeft /> Back to Options
          </button>
          <h1>My Submissions</h1>
          <p>View and track your logbook entries</p>
        </div>

        {success && <div className="alert alert-success">{success}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        <div className="filters-container">
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button 
              className="filter-toggle-btn"
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaFilter /> Filters {showFilters ? <FaTimes /> : ''}
            </button>
            
            {/* Download PDF Button */}
            <button
              className="btn-download-pdf"
              onClick={() => setShowDownloadOptions(true)}
              disabled={isGeneratingPDF || submittedEntries.filter(e => e.status !== 'draft').length === 0}
            >
              <FaDownload /> {isGeneratingPDF ? 'Generating PDF...' : 'Download Logbook PDF'}
            </button>
          </div>

          {showDownloadOptions && createPortal(
            <div className="download-options-overlay" onClick={() => setShowDownloadOptions(false)}>
              <div className="download-options-modal" onClick={e => e.stopPropagation()}>
                <div className="download-options-header">
                  <h3>Download Logbook PDF</h3>
                  <button className="download-options-close" onClick={() => setShowDownloadOptions(false)}>
                    <FaTimes />
                  </button>
                </div>
                <p className="download-options-subtitle">Choose whether to include the submission date column</p>
                <div className="download-options-actions">
                  <button
                    className="download-options-btn"
                    onClick={() => handleDownloadPDF(true)}
                  >
                    Download with submission date
                  </button>
                  <button
                    className="download-options-btn"
                    onClick={() => handleDownloadPDF(false)}
                  >
                    Download without submission date
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {showFilters && (
            <div className="filters-panel">
              <div className="filter-group">
                <label htmlFor="month-filter" style={{ cursor: 'default' }}>Month:</label>
                <div 
                  className="month-input-wrapper"
                  onClick={() => {
                    if (!showAllTime && monthInputRef.current) {
                      monthInputRef.current.focus();
                      monthInputRef.current.showPicker();
                    }
                  }}
                  style={{ cursor: showAllTime ? 'not-allowed' : 'pointer' }}
                >
                  <input
                    ref={monthInputRef}
                    id="month-filter"
                    type="month"
                    value={filters.month}
                    onChange={(e) => setFilters({...filters, month: e.target.value})}
                    max={new Date().toISOString().slice(0, 7)}
                    disabled={showAllTime}
                  />
                </div>
              </div>

              <div className="filter-group">
                <label>Module:</label>
                <select 
                  value={filters.moduleId}
                  onChange={(e) => setFilters({...filters, moduleId: e.target.value})}
                >
                  <option value="">All Modules</option>
                  {modules.map(module => (
                    <option key={module.moduleId} value={module.moduleId}>
                      {module.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Status:</label>
                <select 
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                >
                  <option value="">All Statuses</option>
                  <option value="draft">Draft</option>
                  <option value="submitted">Submitted</option>
                  <option value="under_review">Under Review</option>
                  <option value="approved">Approved</option>
                  <option value="needs_revision">Needs Revision</option>
                  <option value="flagged">Repeat/Remedial</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Sort by Date:</label>
                <button 
                  className="sort-btn"
                  onClick={() => setFilters({
                    ...filters, 
                    sortOrder: filters.sortOrder === 'desc' ? 'asc' : 'desc'
                  })}
                >
                  {filters.sortOrder === 'desc' ? <FaSortAmountDown /> : <FaSortAmountUp />}
                  {filters.sortOrder === 'desc' ? ' Newest First' : ' Oldest First'}
                </button>
              </div>

              <button 
                className="clear-filters-btn"
                onClick={() => setFilters({ 
                  moduleId: '', 
                  sortOrder: 'desc', 
                  status: '',
                  month: new Date().toISOString().slice(0, 7)
                })}
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {/* Month Display and Show All Time Option */}
        <div className="entries-info-bar">
          <div className="entries-month-display">
            {showAllTime ? (
              <span className="month-text">Showing all entries (all time)</span>
            ) : (
              <span className="month-text">
                Showing entries for the month: <strong>{formatMonthName(filters.month)}</strong>
              </span>
            )}
          </div>
          <button 
            className={`btn-toggle-all-time ${showAllTime ? 'active' : ''}`}
            onClick={() => setShowAllTime(!showAllTime)}
          >
            {showAllTime ? 'Show Current Month' : 'Show All Time'}
          </button>
        </div>

        <div ref={listContentRef} className="el-list-content">
          {filteredEntries.length === 0 ? (
            <div className="no-submissions">
              <FaBookMedical className="no-submissions-icon" />
              <h3>No submissions found</h3>
              <p>{submittedEntries.length === 0
                ? 'Start by adding your first competency entry'
                : 'Try adjusting your filters'
              }</p>
              {submittedEntries.length === 0 && !subscriptionExpired && (
                <button onClick={() => navigateToView('select-module')} className="btn-add-first">
                  <FaPlus /> Add First Entry
                </button>
              )}
            </div>
          ) : (
            showAllTime ? renderGroupedByMonth() : renderFlatPaginated()
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default ELogbook;